import { GameId, GAME_METADATAS } from '@mental-mint/shared-types';
import { Game, CommonPageId } from '@mental-mint/shared-ui';
import { 
  waitForGrowthBookReady, 
  getEmailModalVariant, 
  EmailModalVariant,
  authManager,
  logger,
  GetTodaysDate
} from '@mental-mint/shared-utils';

// Word-Drift Assets
import { wordDriftIconLarge } from '@mental-mint/shared-ui';
import taptionaryTitleArt from "./Assets/taptionary-title-art.png";
import { wordDriftPageConfig, WordDriftPageId } from "./Pages/WordDriftPageTypes";
import { wordDriftPageRegistry } from "./Pages/WordDriftPageRegistry";
import { getWordDriftLevelId } from "./WordDriftUtils";
import { loadWordDriftGame } from "./Utils/saveManager";

// WordDrift/Taptionary game configuration
export const WORD_DRIFT_GAME: Game = {
  metadata: GAME_METADATAS[GameId.Taptionary],

  // Page system configuration
  pageConfigs: wordDriftPageConfig,
  initialPageId: WordDriftPageId.Play,
  pageRegistry: wordDriftPageRegistry,

  // Assets
  gameIconLarge: wordDriftIconLarge,
  howToPlayGif: undefined,
  howToPlayText: "Get from one word to another by tapping through the dictionary! Click on words in the definition to navigate.",
  titleArt: taptionaryTitleArt,

  // Fonts, colors, etc.
  fontPrimary: "cubano, sans-serif",
  fontSecondary: "Open Sans",
  colorPrimary: "rgba(139, 69, 19, 1)",
  colorSecondary: "rgb(50, 50, 50)",
  colorUiBackground: "rgba(236, 232, 216, 1)", // Light beige matching the game's paper texture
};

/**
 * Get the word drift game configuration with initial modals determined dynamically
 * based on A/B test variant and whether today's puzzle is already solved.
 * 
 * Returns an array of modal page IDs to show in sequence on first load.
 */
export async function getWordDriftGame(): Promise<Game> {
  const initialModalPageIds: string[] = [];
  
  try {
    await waitForGrowthBookReady();
    const variant = await getEmailModalVariant();
    logger.info('[A/B Test] Email modal variant assigned:', variant);
    
    // Check if today's daily puzzle is already solved
    const saveData = await loadWordDriftGame();
    const todaysDate = GetTodaysDate();
    const todaysLevelId = getWordDriftLevelId(todaysDate);
    const todaysScore = saveData.levelScores[todaysLevelId];
    const isTodaysPuzzleSolved = todaysScore?.isWon === true;
    
    logger.debug('[GameSettings] Today\'s puzzle status:', { 
      levelId: todaysLevelId, 
      solved: isTodaysPuzzleSolved 
    });
    
    // If puzzle is already solved, show PostGame modal immediately
    if (isTodaysPuzzleSolved) {
      logger.debug('[GameSettings] Today\'s puzzle already solved - showing PostGame');
      initialModalPageIds.push(WordDriftPageId.PostGame);
      return {
        ...WORD_DRIFT_GAME,
        initialModalPageIds
      };
    }
    
    // If user should see login prompt on load, add it first
    if (variant === EmailModalVariant.OnPageLoad && !authManager.isAuthenticated()) {
      logger.debug('[A/B Test] Will show login modal on page load');
      initialModalPageIds.push(CommonPageId.Login);
    }
    
    // Always show puzzle intro after login modal (if applicable)
    initialModalPageIds.push(WordDriftPageId.PuzzleIntro);
    
    if (initialModalPageIds.length > 0) {
      logger.debug('[GameSettings] Initial modals to show:', initialModalPageIds);
    }
  } catch (error) {
    logger.error('[Initialization] Failed to determine initial modals:', error);
    // Fallback to just showing puzzle intro
    initialModalPageIds.push(WordDriftPageId.PuzzleIntro);
  }
  
  return {
    ...WORD_DRIFT_GAME,
    initialModalPageIds
  };
}
